import React from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';

/**
 * A small, non-interactive map of one trail's segments — the "screenshot" shown
 * beside a meeting topic. Rendered live rather than as a stored image so it
 * always reflects the current geometry, and it reuses the tile layer the main
 * map already loads.
 */

function FitToSegments({ segments }) {
  const map = useMap();
  React.useEffect(() => {
    const coords = [];
    const extract = (c) => {
      if (typeof c[0] === 'number') coords.push([c[1], c[0]]);
      else c.forEach(extract);
    };
    segments.forEach((s) => { if (s.geometry && s.geometry.coordinates) extract(s.geometry.coordinates); });
    if (coords.length > 0) map.fitBounds(coords, { padding: [18, 18], maxZoom: 16 });
  }, [segments, map]);
  return null;
}

export default function SegmentMiniMap({ trailName, segments, onRemove }) {
  const withGeometry = segments.filter((s) => s.geometry);
  if (withGeometry.length === 0) return null;

  // One link is enough when a trail has many segments; more than that is noise.
  const linkTarget = withGeometry[0].segment_id;
  const numbers = withGeometry
    .map((s) => s.segment_number)
    .filter((n) => n != null)
    .sort((a, b) => a - b);

  return (
    <div className="topic-map">
      <div className="topic-map-head">
        <span className="topic-map-title">🗺 {trailName}</span>
        <span className="topic-map-count">
          {withGeometry.length === 1
            ? `Segment ${numbers[0] ?? ''}`.trim()
            : `${withGeometry.length} segments`}
        </span>
        {onRemove && (
          <button className="topic-map-remove" onClick={onRemove} title="Remove from this topic">✕</button>
        )}
      </div>

      <div className="topic-map-canvas">
        <MapContainer
          center={[33.56, -117.32]}
          zoom={14}
          zoomControl={false}
          scrollWheelZoom={false}
          dragging={false}
          doubleClickZoom={false}
          touchZoom={false}
          keyboard={false}
          attributionControl={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitToSegments segments={withGeometry} />
          {withGeometry.map((s) => (
            <GeoJSON
              key={s.segment_id}
              data={s.geometry}
              style={() => ({ color: '#e63946', weight: 4, opacity: 0.95 })}
            />
          ))}
        </MapContainer>
      </div>

      <Link className="topic-map-link" to={`/?segment=${linkTarget}`}>
        Open on the full map →
      </Link>
    </div>
  );
}
